import { useEffect, useState } from "react";
import { Button, Form, Offcanvas, Spinner } from "react-bootstrap";
import { BoxArrowLeft, PersonDashFill, PersonPlusFill } from "react-bootstrap-icons";
import { useTranslation } from "react-i18next";
import { fetchFriendList } from "../../../../functions/friends";
import { addUserToGameSession, getGameSessionWithUsers, leaveGameSession, removeUserFromGameSession } from "../../../../functions/gameSession";

function GameSessionShareOffcanvas({ show, onClose, sessionId, sessionName, token, onError, onLeave }) {
    const { t } = useTranslation('messages');
    const [sessionDetail, setSessionDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [friends, setFriends] = useState([]);
    const [loadingFriends, setLoadingFriends] = useState(false);
    const [selectedFriendUserId, setSelectedFriendUserId] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!show || !sessionId) return;
        setSessionDetail(null);
        setSelectedFriendUserId("");

        setLoadingDetail(true);
        getGameSessionWithUsers(
            token,
            (data) => {
                setSessionDetail(data.gameSession);
                setLoadingDetail(false);
            },
            (errorCode, errorDescription) => { setLoadingDetail(false); onError(errorCode, errorDescription); },
            sessionId
        );

        setLoadingFriends(true);
        fetchFriendList(
            token,
            (data) => {
                setFriends(data.friends || []);
                setLoadingFriends(false);
            },
            (errorCode, errorDescription) => { setLoadingFriends(false); onError(errorCode, errorDescription); }
        );
    }, [show, sessionId, token, onError]);

    if (!show) return null;

    const sharedUsers = sessionDetail?.users || [];
    const sharedUserIds = new Set(sharedUsers.map(u => u.userId));
    const addableFriends = friends.filter(f => !sharedUserIds.has(f.userId));

    function onAddUser() {
        if (!selectedFriendUserId) return;
        setSaving(true);
        addUserToGameSession(
            token,
            (data) => {
                setSessionDetail(data.gameSession);
                setSelectedFriendUserId("");
                setSaving(false);
            },
            (errorCode, errorDescription) => { setSaving(false); onError(errorCode, errorDescription); },
            sessionId,
            selectedFriendUserId
        );
    }

    function onLeaveClicked() {
        leaveGameSession(token, onLeave, onError, sessionId);
    }

    function onRemoveUser(friendUserId) {
        removeUserFromGameSession(
            token,
            (data) => setSessionDetail(data.gameSession),
            onError,
            sessionId,
            friendUserId
        );
    }

    return (
        <Offcanvas show={show} onHide={onClose} placement="bottom">
            <Offcanvas.Header closeButton data-bs-theme="dark"
                style={{ backgroundColor: '#2d1b4e', color: '#fff' }}>
                <Offcanvas.Title style={{ color: '#fff' }}>
                    {t('game_sessions.share_title')} — {sessionName}
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body style={{ backgroundColor: '#fdf4ff' }}>

                {/* Current shared users */}
                <p className="fw-semibold small text-muted text-uppercase mb-2">
                    {t('game_sessions.share_shared_with')}
                </p>
                {loadingDetail ? (
                    <Spinner animation="border" size="sm" className="mb-3" />
                ) : sharedUsers.length === 0 ? (
                    <p className="text-muted small mb-3">{t('game_sessions.share_no_users')}</p>
                ) : (
                    <div className="d-flex flex-column gap-2 mb-4">
                        {sharedUsers.map((user) => (
                            <div key={user.userId}
                                className="d-flex align-items-center justify-content-between p-2 bg-white rounded-3 shadow-sm">
                                <div>
                                    <span className="fw-semibold small">{user.firstname} {user.lastname}</span>
                                    <span className="text-muted small ms-2">@{user.username}</span>
                                </div>
                                <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => onRemoveUser(user.userId)}
                                    title={t('game_sessions.share_remove_button')}>
                                    <PersonDashFill size={14} />
                                </Button>
                            </div>
                        ))}
                    </div>
                )}

                {/* Add a friend */}
                <p className="fw-semibold small text-muted text-uppercase mb-2">
                    {t('game_sessions.share_add_friend')}
                </p>
                {loadingFriends ? (
                    <Spinner animation="border" size="sm" />
                ) : addableFriends.length === 0 ? (
                    <p className="text-muted small">{t('game_sessions.share_no_friends')}</p>
                ) : (
                    <div className="d-flex gap-2">
                        <Form.Select
                            value={selectedFriendUserId}
                            onChange={(e) => setSelectedFriendUserId(e.target.value)}
                            className="flex-grow-1">
                            <option value="">{t('game_sessions.share_select_placeholder')}</option>
                            {addableFriends.map((f) => (
                                <option key={f.friendId} value={f.userId}>
                                    {f.firstname} {f.lastname} (@{f.username})
                                </option>
                            ))}
                        </Form.Select>
                        <Button
                            variant="outline-primary"
                            onClick={onAddUser}
                            disabled={!selectedFriendUserId || saving}
                            title={t('game_sessions.share_add_button')}>
                            {saving
                                ? <Spinner animation="border" size="sm" />
                                : <PersonPlusFill size={16} />}
                        </Button>
                    </div>
                )}
                <hr />
                <Button variant="outline-warning" size="sm" onClick={onLeaveClicked}>
                    <BoxArrowLeft size={14} className="me-1" />
                    {t('game_sessions.leave_button')}
                </Button>
            </Offcanvas.Body>
        </Offcanvas>
    );
}

export default GameSessionShareOffcanvas;
